<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Defining_Scope_Rules/Namespace_Imports';
    let nextLink= '/Documentation/Defining_Scope_Rules/The_Scoper_Definition';
    
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="namespace-alternatives-1"
  bind:intersecting={visibleById["namespace-alternatives-1"]}
  onIntersectChange={(visible) => setVisible("namespace-alternatives-1", visible)}
>Namespace Alternatives</SectionComponent>
<p>There are occasions where you do not want to adhere to the hierarchical nature of namespaces. For example,
when you want to implement a qualified name in your DSL, like <code>moduleA.partB</code>, where what comes after the dot
is something that is visible in <code>moduleA</code>, but possibly not in the namespace
in which this expression is located. </p>
<p>This can be done using namespace alternatives. Where namespace imports
add nodes to the set of visible nodes, a namespace alternative both <em>removes and adds</em> nodes. What is removed are the
visible nodes from the parent namespace. In the case of the expression <code>moduleA.partB</code> this would be the namespace
in which the expression resides. What is added is another namespace (the alternative), which in the example
would be the namespace identified by <code>moduleA</code>.</p>
<SectionComponent tag="h2" 
  id="effect-on-the-namespace-tree-2"
  bind:intersecting={visibleById["effect-on-the-namespace-tree-2"]}
  onIntersectChange={(visible) => setVisible("effect-on-the-namespace-tree-2", visible)}
>Effect on the Namespace Tree</SectionComponent>
<p>The effect of declaring a namespace alternative on the namespace tree is that the link of the namespace with its parent is
removed, and a link to another namespace is made.
This second namespace is then used as if it were an imported namespace, i.e. only the non-private (public), declared nodes become visible.</p>
<p>For instance, suppose in the following figure that node Z1 has a namespace replacement to node A6, as shown by the green arrow.
In that case, the link between Z1 and its parent namespace A1 is broken. The visible nodes of Z1 are its
declared nodes ([H1, J1, A7]), plus the declared nodes of A6 ([A6, F1, D6]).</p>
<Figure
imageName={'documentation/AST-graph-with-alternative.png'}
caption={'AST with namespace alternative'}
figureNumber={1}
/>
<p>When we leave out the AST nodes, the difference to the namespace tree, will become clearer. Note that similar to the effect
of a namespace import, the effect of a namespace alternative is that the namespace tree is changed into a graph.</p>
<Figure
imageName={'documentation/NS-graph-with-alternative.png'}
caption={'Namespace Graph with alternative'}
figureNumber={2}
/>
<SectionComponent tag="h2" 
  id="recursive-alternatives-3"
  bind:intersecting={visibleById["recursive-alternatives-3"]}
  onIntersectChange={(visible) => setVisible("recursive-alternatives-3", visible)}
>Recursive Alternatives</SectionComponent>
<p>Sometimes you may want to include not only the declared nodes of the alternative namespace, but its alternative
nodes as well. We can define this in the scope
file by adding the keyword <code>recursive</code> in front of the alternative namespace. Each alternative in the list can
have its own keyword.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/scoper-docu.scope#L11-L15

AttributeRef &#123;
	alternatives &#123;
        self.owner().type();
    &#125; 
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="example-a-qualified-name-4"
  bind:intersecting={visibleById["example-a-qualified-name-4"]}
  onIntersectChange={(visible) => setVisible("example-a-qualified-name-4", visible)}
>Example: A Qualified Name</SectionComponent>
<p>Let’s explore the case we mentioned earlier, where you want a qualified name in your DSL, like <code>moduleA.partB</code>. Suppose
you would also like to
be able to address local variables without the prefix, like <code>localVarA</code>. In that case you need to
include concepts like the following in your .ast file. A <code>SimpleVarReference</code> represents a reference to local variables,
a <code>ComplexVarReference</code> represents a reference with a prefix. To be prepared for the future where you might want
to have modules within modules the target of a <code>ComplexVarReference</code> is either a <code>SimpleVarReference</code> (no prefix), or
a <code>ComplexVarReference</code> (adding another prefix). The structure of your language would look like this:</p>
<pre class="language-freon"><code class="language-freon">// QName/src/defs/LanguageWithScopes.ast#L21-L31

abstract concept VarReference &#123;
&#125;

concept ComplexVarReference base VarReference &#123;
    target: VarReference;
    reference module: Module;
&#125;

concept SimpleVarReference base VarReference &#123;
    reference target: Variable;
&#125;</code></pre>
<p>Now the scoper definition can be as follows.</p>
<pre class="language-freon"><code class="language-freon">// QName/src/defs/LanguageWithScopes.scope

scoper for language QName

isNamespace &#123; Module, VarReference &#125;

VarReference &#123;
    alternatives &#123;
        recursive owner().if(Module);
        owner().if(ComplexVarReference).module;
    &#125;
&#125;
</code></pre>
<p>Both <code>Module</code> and <code>VarReference</code> are namespaces, which means that we can define what is visible in both contexts.
For <code>Module</code> we simply used the standard hierarchical manner of building namespaces. But <code>VarReference</code> is different.
An instance of <code>VarReference</code> resides within a <code>Module</code>, so when the hierarchy of namespaces is used, everything
that is visible in this <code>Module</code> would be visible in the <code>VarReference</code>. That is fine, when you want to refer to a local variable,
because the local variables would indeed be visible in the surrounding <code>Module</code>. But it is not okay, when we want to refer
to <code>partA</code> from <code>ModuleA</code>. In such an instance of a <code>ComplexVarReference</code> only what is visible in <code>ModuleA</code> should be visible.</p>
<p>So, we distinguish between these two cases by creating two alternative namespaces. The first expression is actually
recreating the hierarchy of namespaces but <em>only</em> when the owning namespace is an instance of Module. Then the visible nodes
consist of everything declared in this owning namespace, and because it is recursive, we get the nodes from its parents as well.
The second expression yields a result when the <code>VarReference</code> exists within an instance of <code>ComplexVarReference</code>. In this case
it defines the <code>module</code> property of its owner as its namespace.</p>
<p>We cannot define the scope of <code>ComplexVarReference</code> only, because then a
<code>SimpleVarReference</code> within a <code>ComplexVarReference</code> would still have the scope of its parent, not of the <code>module</code> property of its parent.</p>
<p>To finish this off, let’s revisit the editor definition to get the appearance and behaviour right. When you make the following entries
in the .edit file, any <code>ComplexVarReference</code> would indeed look like <code>moduleA.partB</code>, and any <code>SimpleVarReference</code>
would look like <code>partX</code>. Now, the behaviour that you would normally get is that the user must create either a <code>ComplexVarReference</code>
instance, or a <code>SimpleVarReference</code> instance, and then add the option from the correct list. The behaviour that you would like, is probably
that the user is simply able to select an option from the correct list. The trick to get this behaviour is to add the reference
shortcuts as shown below (See <a
  href="/Documentation/Defining_an_Editor"
>Ease of Editing</a>).</p>
<pre class="language-freon"><code class="language-freon">// QName/src/defs/LanguageWithScopes.edit#L10-L20

ComplexVarReference &#123;[
    $&#123;module&#125;.$&#123;target&#125;
]
referenceShortcut = $&#123;module&#125;
&#125;

SimpleVarReference &#123;[
    $&#123;target&#125;
]
referenceShortcut = $&#123;target&#125;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="scoping-the-algorithm-5"
  bind:intersecting={visibleById["scoping-the-algorithm-5"]}
  onIntersectChange={(visible) => setVisible("scoping-the-algorithm-5", visible)}
>Scoping: The Algorithm</SectionComponent>
<p>As promised, here is the complete algorithm for building the namespace graph in pseudocode.</p>
<pre class=""><code class="">FreNamespace &#123;
    getDeclaredNodes(publicOnly: boolean): FreNamedNode[] &#123;
        return all AST nodes in the subtree of which this namespace is the top,
        and the leaves are AST nodes that are themselves namespaces.
        The parameter 'publicOnly' indicates whether to include AST nodes that are marked private.
        The constant 'ALL' indicates that all nodes should be included, the constant 'PUBLIC_ONLY'
        indicates the opposite.
    &#125;
    getParentNodes(): FreNamedNode[] &#123;
        THIS.parentNamespace.getVisibleNodes();
    &#125;
    getImportedNodes(list: FreNamespaceInfo[]): FreNamedNode[] &#123;
        list.forEach(import =&gt; &#123;
            import.namespace.getDeclaredNodes(PUBLIC_ONLY)
        plus
            if (import is recursive) &#123;
                import.namespace.getImportedNodes(import.namespace.imports)
            &#125;
        &#125;)
    &#125;
    getAlternativeNodes(): FreNamedNode[] &#123;
        getDeclaredNodes(ALL) plus
        getImportedNodes(THIS.alternatives)
    &#125;
    getVisibleNodes(): FreNamedNode[] &#123;
        if (has replacement) then
            getAlternativeNodes()
        else
            getDeclaredNodes(ALL) plus
            getParentNodes() plus
            getImportedNodes(THIS.imports)
        endif
    &#125;
&#125;
</code></pre>
<p>With all this knowledge, we are finally able to explain how to write a Freon scoper definition. You can find this on the next page.</p>


<PrevNextSection {prevLink} {nextLink} />